import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const localNow = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const blank = () => ({ channel: "call", direction: "incoming", contact_name: "", contact_phone: "", contact_email: "", subject: "", notes: "", status: "open", follow_up_date: "", project_id: "", received_at: localNow() });

const Pick = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const CommForm = ({ open, onOpenChange, entry, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(blank());
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(entry ? { ...blank(), ...entry, received_at: toLocal(entry.received_at) } : blank());
    api("get", "/projects").then(setProjects);
  }, [open, entry, api]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, received_at: f.received_at ? new Date(f.received_at).toISOString() : "" };
    delete body.id; delete body.logged_by; delete body.created_at; delete body.project_name;
    try {
      onSaved(entry ? await api("put", `/communications/${entry.id}`, body) : await api("post", "/communications", body));
      onOpenChange(false);
      toast.success("Saved to contact log");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{entry ? "Edit contact" : "Log a call, text or email"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="comm-form">
          <Pick label="Type" value={f.channel} onChange={(v) => setF({ ...f, channel: v })} testId="comm-channel-select"
            options={[["call", "Phone call"], ["text", "Text message"], ["email", "Email"], ["voicemail", "Voicemail"], ["in_person", "In person"], ["other", "Other"]]} />
          <Pick label="Direction" value={f.direction} onChange={(v) => setF({ ...f, direction: v })} testId="comm-direction-select" options={[["incoming", "Received"], ["outgoing", "Sent / made"]]} />
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={f.contact_name} onChange={set("contact_name")} data-testid="comm-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={f.contact_phone} onChange={set("contact_phone")} data-testid="comm-phone-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={f.contact_email} onChange={set("contact_email")} data-testid="comm-email-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">When</Label><Input type="datetime-local" value={f.received_at} onChange={set("received_at")} data-testid="comm-when-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Subject</Label><Input value={f.subject} onChange={set("subject")} placeholder="e.g. Wants a quote for a 20x30 slab" data-testid="comm-subject-input" /></div>
          <div className="sm:col-span-2"><Textarea value={f.notes} onChange={set("notes")} placeholder="Notes" data-testid="comm-notes-input" /></div>
          <Pick label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} testId="comm-status-select" options={[["open", "Open"], ["follow_up", "Needs follow-up"], ["resolved", "Resolved"]]} />
          <div className="space-y-1.5"><Label className="text-xs">Follow-up date</Label><Input type="date" value={f.follow_up_date} onChange={set("follow_up_date")} data-testid="comm-followup-input" /></div>
          <div className="sm:col-span-2">
            <Pick label="Related project (optional)" value={f.project_id || "none"} onChange={(v) => setF({ ...f, project_id: v === "none" ? "" : v })} testId="comm-project-select"
              options={[["none", "No project"], ...projects.map((p) => [p.id, p.name])]} />
          </div>
          <button className="rounded-full bg-[#F2B91C] py-3 text-sm font-bold text-[#0E1F3F] hover:bg-[#D9A30F] sm:col-span-2" data-testid="comm-save-btn">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
